/**
 * Souqli listing AI (heuristics, no external API).
 *
 * Shared by the standalone AI listing tool and the Shopify-style Sell page.
 * Detects brand / category / condition / size / colour / price from free text,
 * builds clean titles and tone-aware descriptions, and generates tags + SEO.
 */
import { garmentType, productColour, type Garment } from '@/lib/catalog';
import {
  classifyListing, detectCategory, detectCondition, conditionOptions,
  conditionPhrase, conditionAxisLabel, isGadget, type Condition,
} from '@/lib/smartListing';

// Re-export the smart agent's surface so existing importers keep working.
export {
  classifyListing, detectCategory, detectCondition, conditionOptions,
  conditionPhrase, conditionAxisLabel, isGadget,
};
export type { Condition };

export type Tone = 'friendly' | 'professional' | 'luxury' | 'minimal' | 'genz';

export const TONES: { v: Tone; label: string; icon: string }[] = [
  { v: 'friendly',     label: 'Friendly',     icon: '😊' },
  { v: 'professional', label: 'Professional', icon: '👔' },
  { v: 'luxury',       label: 'Luxury',       icon: '💎' },
  { v: 'minimal',      label: 'Minimal',      icon: '▪️' },
  { v: 'genz',         label: 'Gen-Z',        icon: '🔥' },
];

export { garmentType };
export type { Garment };

const BRANDS = [
  'Apple', 'iPhone', 'iPad', 'MacBook', 'AirPods', 'Samsung', 'Galaxy', 'Sony', 'Bose', 'Dyson',
  'Nintendo', 'PlayStation', 'Xbox', 'Google Pixel', 'Microsoft', 'Dell', 'HP', 'Lenovo', 'Canon', 'Nikon',
  'Nike', 'Adidas', 'New Balance', 'Puma', 'Reebok', 'Converse', 'Vans', 'Asics', 'Salomon', 'On Running',
  'Burberry', 'Gucci', 'Prada', 'Louis Vuitton', 'Loewe', 'Mulberry', 'Bottega Veneta', 'Saint Laurent',
  'Balenciaga', 'Hermes', 'Chanel', 'Dior', 'Fendi', 'Celine', 'Valentino', 'Versace', 'Givenchy',
  'Acne Studios', 'Max Mara', 'COS', 'Arket', 'Ganni', 'Toteme', 'Reiss', 'Whistles', 'Sandro', 'Maje',
  'Ralph Lauren', 'Tommy Hilfiger', 'Lacoste', 'Stone Island', 'Carhartt', 'The North Face', 'Patagonia',
  'Barbour', 'Levi', 'Wrangler', 'Uniqlo', 'Zara', 'Mango', 'H&M', 'ASOS', 'Massimo Dutti',
  'Rolex', 'Omega', 'Tag Heuer', 'Tudor', 'Seiko', 'Cartier', 'Casio', 'Tissot', 'Garmin',
  'Jo Malone', 'Tom Ford', 'Creed', 'Chloe', 'Marc Jacobs', 'YSL',
];

export function detectBrand(t: string): string | undefined {
  const lower = t.toLowerCase();
  for (const b of BRANDS) {
    if (lower.includes(b.toLowerCase())) {
      if (['iphone', 'ipad', 'macbook', 'airpods'].includes(b.toLowerCase())) return 'Apple';
      if (b.toLowerCase() === 'galaxy') return 'Samsung';
      if (b.toLowerCase() === 'google pixel') return 'Google';
      if (b.toLowerCase() === 'levi') return 'Levi’s';
      if (b.toLowerCase() === 'ysl') return 'Saint Laurent';
      return b;
    }
  }
  return undefined;
}

const STORAGE_RE = /\b(\d{2,4}\s?gb|\d\s?tb)\b/i;
const VOLUME_RE = /\b(\d{1,4}\s?ml|\d(?:\.\d)?\s?oz)\b/i;
const SIZE_RE = /\b(uk\s?\d{1,2}|eu\s?\d{2}|us\s?\d{1,2}|size\s?\d{1,2}|\b(?:xs|s|m|l|xl|xxl)\b)\b/i;

/**
 * Best-effort size / spec detection from the seller's description:
 *  - phones / laptops -> storage (256GB, 1TB)
 *  - fragrance and anything sold by volume -> millilitres / ounces (100ml)
 *  - apparel / shoes -> UK/EU/US number or XS-XXL
 * Returns '' when nothing is stated - the seller then fills the (required) box.
 */
export function detectSize(t: string, garment: Garment): string {
  if (garment === 'phone' || garment === 'laptop') {
    const m = t.match(STORAGE_RE);
    if (m) return m[1].toUpperCase().replace(/\s+/g, '');
  }
  const vol = t.match(VOLUME_RE);
  if (vol) return vol[1].toLowerCase().replace(/\s+/g, '');
  const s = t.match(SIZE_RE);
  if (s) return s[1].replace(/\s+/g, ' ').replace(/^size\s?/i, '').toUpperCase();
  return '';
}

export function detectPrice(t: string): number | undefined {
  const m = t.match(/£\s?(\d{1,5})(?:\.\d{1,2})?/);
  return m ? Number(m[1]) : undefined;
}

export function detectColour(t: string): string | undefined {
  return productColour({ title: t });
}

const TITLE_STOP = /\b(brand[- ]?new|bnwt|like[- ]?new|worn once|excellent|good condition|well[- ]?loved|used|pre[- ]?owned|pre[- ]?loved|second[- ]?hand|comes? with|includes?|smoke[- ]?free|pet[- ]?free|selling|for sale|barely (?:worn|used)|no (?:box|tags?))\b/gi;

export function buildTitle(raw: string, brand?: string): string {
  let core = raw.split(/[,.\n]/)[0] || raw;
  core = core.replace(TITLE_STOP, ' ').replace(/£\s?\d+(?:\.\d+)?/g, ' ').replace(/\s+/g, ' ').trim();
  core = core.split(' ')
    .map((w) => (/[0-9]/.test(w) || w.length <= 2 ? w : w.charAt(0).toUpperCase() + w.slice(1)))
    .join(' ').trim();
  if (brand && !core.toLowerCase().includes(brand.toLowerCase().split(' ')[0])) core = `${brand} ${core}`.trim();
  if (!core) core = brand ?? 'Pre-loved item';
  return core.slice(0, 80);
}

const CATEGORY_NOUN: Record<string, string> = {
  womenswear: 'piece', menswear: 'piece', shoes: 'pair', bags: 'bag',
  electronics: 'device', watches: 'watch', kids: 'kids piece', fragrance: 'fragrance',
  traditional: 'piece', accessories: 'accessory',
};

const cap = (s: string) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);
const listWords = (a: string[]) => (a.length <= 1 ? a[0] ?? '' : `${a.slice(0, -1).join(', ')} and ${a[a.length - 1]}`);

function detectExtras(t: string): string[] {
  const lower = t.toLowerCase();
  const out: string[] = [];
  for (const [re, name] of [
    [/\bbox\b/, 'the original box'], [/\btags?\b/, 'tags'], [/\breceipt\b/, 'the receipt'],
    [/\bcable\b/, 'the cable'], [/\bcharger\b/, 'the charger'], [/\bcase\b/, 'a case'],
    [/\bdust ?bag\b/, 'the dust bag'], [/\bpapers?\b|certificate/, 'authenticity papers'],
  ] as [RegExp, string][]) if (re.test(lower)) out.push(name);
  return out;
}
function detectFlaws(t: string): string[] {
  const lower = t.toLowerCase();
  const out: string[] = [];
  for (const [re, name] of [
    [/\bscratch/, 'a few light scratches'], [/\bscuff/, 'minor scuffs'], [/\bstain/, 'a small mark'],
    [/\bcrack/, 'a hairline crack'], [/\bfade|faded/, 'slight fading'], [/\btear|rip\b/, 'a small repair'],
  ] as [RegExp, string][]) if (re.test(lower)) out.push(name);
  return out;
}

export interface DescFields {
  brand?: string; title?: string; category?: string; colour?: string;
  condition: Condition; size?: string; notes?: string; garment?: Garment;
}

/** Deterministic pick so the same item always reads the same (no churn on re-render). */
function pick<T>(arr: T[], seed: string): T {
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) | 0;
  return arr[Math.abs(h) % arr.length];
}

/** A tailored styling or use suggestion, so the copy reads specific, not generic. */
function stylingLine(f: DescFields, g: Garment): string {
  const gs = String(g);
  const cat = f.category ?? '';
  const seed = `${f.title ?? ''}${f.colour ?? ''}style`;
  if (gs === 'shoes' || cat === 'shoes') return pick([
    'Dress them up with tailoring or down with your favourite jeans.',
    'They sit as happily under a suit as with a weekend outfit.',
  ], seed);
  if (gs === 'bag' || cat === 'bags') return 'Carry it from desk to dinner without missing a beat.';
  if (gs === 'watch' || cat === 'watches') return 'Wear it daily, or save it for the occasions that call for a little polish.';
  if (cat === 'womenswear' || cat === 'menswear') return pick([
    'Easy to dress up or down, it slots straight into your everyday rotation.',
    'Layer it, build around it, and let it do the quiet work in an outfit.',
  ], seed);
  if (cat === 'traditional') return 'A striking choice for the celebrations and occasions that deserve something memorable.';
  return '';
}

/**
 * Tone-aware description writer.
 *
 * Composes a detailed, well-structured blurb from everything detected: an
 * engaging opener, an honest and specific condition line, a tailored appeal,
 * a styling or use suggestion, size/fit, what is included, any flaws (stated
 * plainly), and a reassuring close. Reads like a person wrote it, stays offline.
 */
export function generateDescription(f: DescFields, tone: Tone = 'friendly'): string {
  const g = f.garment ?? garmentType({ title: `${f.title ?? ''} ${f.brand ?? ''}`, category: f.category });
  // Read sealed / fragrance-fill signals from the seller's own notes so the
  // wording matches the item (sealed, pre-owned, "% full"), not just "worn".
  const sig = classifyListing(`${f.notes ?? ''} ${f.title ?? ''}`);
  const cond = conditionPhrase(f.condition, f.category, g, { sealed: sig.sealed, fillPercent: sig.fillPercent });
  const noun = g && g !== 'other' ? g.replace('tshirt', 't-shirt') : (CATEGORY_NOUN[f.category ?? ''] ?? 'piece');
  const subject = [f.brand, f.colour, noun].filter(Boolean).join(' ').trim() || 'piece';
  const extras = detectExtras(f.notes ?? '');
  const flaws = detectFlaws(f.notes ?? '');
  const seed = `${f.brand ?? ''}${f.title ?? ''}${subject}${f.condition}`;

  const openers: Record<Tone, string[]> = {
    friendly:     [`Here is a ${subject} that deserves a second life in your wardrobe.`, `A lovely ${subject}, ready for its next chapter.`],
    professional: [`A carefully kept ${subject}, presented in genuinely good order and ready for its next owner.`, `This ${subject} has been well looked after and is offered with nothing hidden.`],
    luxury:       [`An exquisite ${subject}, considered in every detail and beautifully presented.`, `A refined ${subject} for the discerning wardrobe, and a quiet pleasure to own.`],
    minimal:      [`${cap(subject)}. Considered, and kept well.`],
    genz:         [`not me gatekeeping this ${subject} 😭 it's too good`, `ok this ${subject} is actually unreal 🫶`],
  };

  const condLine = cond.body
    ? `In terms of condition, it is ${cond.label.toLowerCase()}: ${cond.body}.`
    : `It is in ${cond.label.toLowerCase()} condition.`;

  const lines: string[] = [pick(openers[tone], seed), condLine];
  const styling = tone === 'minimal' ? '' : stylingLine(f, g);
  if (styling) lines.push(styling);
  if (f.size) lines.push(`Size and fit: ${f.size}.`);
  if (extras.length) lines.push(`It comes with ${listWords(extras)}.`);
  if (flaws.length) lines.push(`In the interest of full honesty, there ${flaws.length === 1 ? 'is' : 'are'} ${listWords(flaws)}, and the price reflects this.`);

  const closers: Record<Tone, string> = {
    friendly:     'It ships quickly and is protected by Souqli, so your payment is held safely until you confirm it has arrived as described. Sensible offers are always welcome.',
    professional: 'Dispatched promptly and covered end to end by Souqli buyer protection, with every seller ID-verified. Reasonable offers are welcome.',
    luxury:       'Dispatched with the utmost care and fully protected by Souqli, every seller ID-verified and every payment held securely until you confirm receipt. Serious enquiries welcome.',
    minimal:      'Ships via Souqli. Payment held safely until you confirm.',
    genz:         'ships fast + protected so zero stress 🙌 offers ok but no lowballs pls',
  };
  lines.push(closers[tone]);
  return lines.join(' ').replace(/\s+/g, ' ').trim().slice(0, 900);
}

/** Marketplace keyword tags for discovery. */
export function generateTags(f: DescFields): string[] {
  const g = f.garment ?? garmentType({ title: `${f.title ?? ''} ${f.brand ?? ''}`, category: f.category });
  const raw = [
    f.brand, f.colour, g !== 'other' ? g : undefined, f.category,
    { NEW_TAGS: 'new with tags', LIKE_NEW: 'like new', EXCELLENT: 'excellent', GOOD: 'good condition', FAIR: 'well loved' }[f.condition],
    'preloved', 'second hand', 'sustainable', f.size ? `size ${f.size}` : undefined,
  ];
  return Array.from(new Set(raw.filter(Boolean).map((s) => String(s).toLowerCase().trim()))).slice(0, 10);
}

/** SEO page title + meta description. */
export function generateSeo(f: DescFields): { title: string; description: string } {
  const bits = [f.brand, f.title, f.colour, f.size].filter(Boolean).join(' ');
  const condWord = { NEW_TAGS: 'New', LIKE_NEW: 'Like New', EXCELLENT: 'Excellent', GOOD: 'Good', FAIR: 'Pre-loved' }[f.condition];
  const title = `${(bits || f.title || 'Pre-loved item').trim()} | ${condWord} | Souqli`.slice(0, 65);
  const description = generateDescription(f, 'professional').replace(/\s+/g, ' ').slice(0, 155).trim();
  return { title, description };
}

export function median(nums: number[]): number | undefined {
  if (!nums.length) return undefined;
  const s = [...nums].sort((a, b) => a - b);
  const mid = Math.floor(s.length / 2);
  return s.length % 2 ? s[mid] : (s[mid - 1] + s[mid]) / 2;
}
